import { z } from 'zod';
import { PILOT_TRACK_ROLES } from '../constants';
import { orgListReferenceSchema } from './contact-book';
import { hubListNameSchema } from './hub-contact-lists';
import { AUTOMATION_WEBHOOK_MOUNT } from './route-paths';
import {
  MONITOR_AUTOMATION_FIRE_ACTION_KINDS,
  MONITOR_AUTOMATION_ITEM_ACTION_KINDS,
  MONITOR_GATE_AGENT_NAME_MAX,
  decodedMonitorConfig,
  isAutomationGateTrigger,
  resolveMonitorTrigger,
  type AutomationGateTrigger,
} from './setup/agents';
import { isDecisionsModel, readAgentSettingsModel } from '../decisions-model';

/**
 * Hub automations (wayai#4931): a trigger starts work in this hub.
 *
 * One automation is a trigger, a target and an action. The vocabulary below is the program's
 * whole D2 shape, declared once so storage, these contracts and `hub.yaml` spell every variant
 * the same way. Which variants a hub can SAVE is narrower, and is decided in one place:
 * `validateAutomation` in `apps/backend/src/automations/validate.ts`, which refuses every variant
 * the fire path does not run, naming the automation. A config the platform cannot fire is
 * refused at save, never stored inert.
 */

export const AUTOMATION_TRIGGER_TYPES = ['schedule', 'event', 'webhook'] as const;
/**
 * The platform events an `event` trigger fires on (wayai#4931 D12): the typed catalog. The hub
 * emits each one where its conversation index makes the transition, never by polling:
 *   - `conversation.flagged`: one of the hub's conversations is flagged — by its monitor or its
 *     evaluator — where it was not flagged before;
 *   - `conversation.closed`: one of the hub's conversations closes, and again each time a reopened
 *     one closes again.
 * An eval run's conversations emit neither.
 */
export const AUTOMATION_EVENT_TYPES = ['conversation.flagged', 'conversation.closed'] as const;
export const AUTOMATION_TARGET_TYPES = ['none', 'contact_list'] as const;
export const AUTOMATION_ACTION_TYPES = ['run_agent', 'send_message'] as const;
export const AUTOMATION_CHANNEL_TYPES = ['whatsapp', 'instagram', 'email', 'app'] as const;
/**
 * Why an automation sends (wayai#4931 D8): a `marketing` send is stopped by a suppression of
 * either scope its recipient holds, an `operational` one (an appointment reminder, say) only by an
 * `all` suppression — so someone who stopped promotions still gets their reminders
 * (`contact-book.ts`, SUPPRESSIONS). An automation that does not say is `marketing`.
 */
export const AUTOMATION_PURPOSES = ['marketing', 'operational'] as const;
export const DEFAULT_AUTOMATION_PURPOSE = 'marketing' satisfies AutomationPurpose;

export type AutomationTriggerType = typeof AUTOMATION_TRIGGER_TYPES[number];
export type AutomationEventType = typeof AUTOMATION_EVENT_TYPES[number];
/**
 * The trigger types a hub can save (`automationTriggerTypesFor`): every type has a fire path —
 * the schedule's, an event's (wayai#4931 D12) and a webhook delivery's (D13).
 */
export type AutomationSavableTriggerType = AutomationTriggerType;

/** Whether `value` is an event of the catalog (`AUTOMATION_EVENT_TYPES`). */
export function isAutomationEventType(value: unknown): value is AutomationEventType {
  return (AUTOMATION_EVENT_TYPES as readonly unknown[]).includes(value);
}
export type AutomationTargetType = typeof AUTOMATION_TARGET_TYPES[number];
export type AutomationActionType = typeof AUTOMATION_ACTION_TYPES[number];
export type AutomationChannelType = typeof AUTOMATION_CHANNEL_TYPES[number];
export type AutomationPurpose = typeof AUTOMATION_PURPOSES[number];

/** The status an automation run records: `running` while it fires, then its outcome. */
export const AUTOMATION_RUN_STATUSES = ['running', 'completed', 'partial', 'failed'] as const;
export type AutomationRunStatus = typeof AUTOMATION_RUN_STATUSES[number];

/**
 * The trigger types a hub of `hubType` may save (wayai#4931 D3, D12, D13): a schedule anywhere; on
 * a task hub only, an event — whose fire opens tasks with the event's conversation beside the
 * admin's instructions — and an external webhook, whose every accepted delivery fires (v1: chat
 * hubs take neither).
 */
export function automationTriggerTypesFor(hubType: string | undefined): AutomationSavableTriggerType[] {
  return hubType === 'task' ? ['schedule', 'event', 'webhook'] : ['schedule'];
}

/**
 * How far a chain of automations reaches through events (wayai#4931 D12, the precedent
 * `MAX_REINVOKE_DEPTH`). A task an automation opens carries its chain depth: one more than that of
 * the conversation whose event fired it, or 1 for a fire with no event (a schedule's, a run-now's);
 * a conversation no automation opened is at 0. An event on a conversation at this depth or deeper
 * fires nothing, so automations that answer each other's tasks stop instead of looping. (An
 * automation never answers its own tasks' events: the hub leaves the automation that opened a
 * conversation out of that conversation's events.)
 */
export const AUTOMATION_CHAIN_MAX_DEPTH = 3;

/**
 * The action types a hub of `hubType` may save (wayai#4931 D3): a task hub's automations run an
 * agent; a chat hub's send a message or run an agent.
 */
export function automationActionTypesFor(hubType: string | undefined): AutomationActionType[] {
  return hubType === 'task' ? ['run_agent'] : ['send_message', 'run_agent'];
}

/**
 * The target types a hub of `hubType` may save (wayai#4931 D3), for a trigger of `triggerType`
 * when one is given. A task hub's fire opens tasks: one per fire (`none`), or one per contact of a
 * list (`contact_list`) — but an event's fire opens one task, for the event's conversation, so an
 * `event` trigger targets `none` only. A chat hub's fire reaches people, so it always targets a
 * list.
 */
export function automationTargetTypesFor(
  hubType: string | undefined,
  triggerType?: AutomationTriggerType,
): AutomationTargetType[] {
  if (hubType !== 'task') return ['contact_list'];
  return triggerType === 'event' ? ['none'] : ['none', 'contact_list'];
}

/**
 * Whether an action of `type` on a hub of `hubType` opens tasks (wayai#4931 D3, D5): a task hub's
 * `run_agent`, each fire's targets being tasks in the hub rather than people. Such an action takes
 * no channel (`automationTakesChannel`), runs an agent that takes a task's turn
 * (`agentTakesTaskTurn`), and is held back by the fire's operations-quota gate.
 */
export function automationOpensTasks(type: AutomationActionType, hubType: string | undefined): boolean {
  return type === 'run_agent' && hubType === 'task';
}

/**
 * Whether an action of `type` on a hub of `hubType` takes a channel: every action does but one that
 * opens tasks (`automationOpensTasks`), whose tasks reach no one through a channel.
 */
export function automationTakesChannel(type: AutomationActionType, hubType: string | undefined): boolean {
  return !automationOpensTasks(type, hubType);
}

/**
 * Whether an agent of `role` can run an automation's `run_agent` turn — a task hub's task
 * (`automationOpensTasks`) or a chat hub's contact's conversation (wayai#4931 D3): the turn runs
 * its conversation's current agent, which only a pilot-track agent takes on the agent track
 * (`resolveTurnAgent` passes over any other and runs the hub's pilot instead).
 */
export function agentTakesTaskTurn(role: unknown): boolean {
  return (PILOT_TRACK_ROLES as readonly unknown[]).includes(role);
}

/**
 * The channels each action fires on where it takes one (`automationTakesChannel`): `send_message`
 * has no Instagram send path yet.
 */
export const AUTOMATION_ACTION_CHANNELS: Readonly<Record<AutomationActionType, readonly AutomationChannelType[]>> = {
  send_message: ['whatsapp', 'email', 'app'],
  run_agent: AUTOMATION_CHANNEL_TYPES,
};

/** The action fields the fire path may read. */
export const AUTOMATION_ACTION_FIELDS = ['connection_id', 'template_id', 'text', 'agent_id', 'instructions'] as const;
export type AutomationActionField = typeof AUTOMATION_ACTION_FIELDS[number];

/**
 * The fields an action reads on `channel` — every other field would be stored inert, so a save
 * refuses it (wayai#4931 D3). `send_message` sends a template from a WhatsApp connection, a text
 * from an email connection, or a text on the app. `run_agent` runs its agent in each contact's
 * conversation on the channel, through that channel's connection — every channel's but the app's
 * — and on WhatsApp it also names the approved template that opens the conversation when the
 * contact's 24-hour window is closed. A task hub's `run_agent` takes no channel
 * (`automationTakesChannel`), so it reads its agent and instructions only.
 */
export function automationActionFields(
  type: AutomationActionType,
  channel: AutomationChannelType | undefined,
): ReadonlySet<AutomationActionField> {
  if (type === 'send_message') {
    if (channel === 'whatsapp') return new Set(['connection_id', 'template_id']);
    return new Set(channel === 'email' ? ['connection_id', 'text'] : ['text']);
  }
  if (channel === 'whatsapp') return new Set(['agent_id', 'instructions', 'connection_id', 'template_id']);
  return new Set(channel && channel !== 'app' ? ['agent_id', 'instructions', 'connection_id'] : ['agent_id', 'instructions']);
}

/**
 * Size bounds. They make `listAutomations` a list with a stateable worst case on both axes
 * (AGENTS.md § Structure & Primitives, the 32 MiB rule): at most `AUTOMATIONS_PER_HUB_MAX` rows,
 * each bounded by these lengths.
 */
export const AUTOMATIONS_PER_HUB_MAX = 200;
export const AUTOMATION_NAME_MAX = 100;
export const AUTOMATION_DESCRIPTION_MAX = 1000;
export const AUTOMATION_INSTRUCTIONS_MAX = 10_000;
export const AUTOMATION_TEXT_MAX = 4096;
export const AUTOMATION_CRON_MAX = 512;

// ---------------------------------------------------------------------------
// The webhook trigger (wayai#4931 D13)
// ---------------------------------------------------------------------------

/**
 * How a webhook trigger's sender proves itself, Rekor's integrator scheme
 * (`skills/wayai/references/bases/integrations.md` § Inbound webhooks):
 *   - `hmac` — every delivery is signed: `X-Data-Signature: v1,<hex>`, an HMAC-SHA256 under the
 *     automation's secret over `["v1", id, timestamp, method, path, body].join("\n")`, with
 *     `X-Data-Timestamp` (unix seconds) inside `AUTOMATION_WEBHOOK_MAX_SKEW_SECONDS` of now and
 *     `X-Data-Id` the message id the signature binds. The published `@wayai/executor-sdk`'s
 *     `signRequest` produces exactly these headers.
 *   - `static_header` — for a sender that cannot sign: the trigger's `header` carries the secret
 *     itself, compared in constant time.
 */
export const AUTOMATION_WEBHOOK_AUTHS = ['hmac', 'static_header'] as const;
export type AutomationWebhookAuth = typeof AUTOMATION_WEBHOOK_AUTHS[number];

export function isAutomationWebhookAuth(value: unknown): value is AutomationWebhookAuth {
  return (AUTOMATION_WEBHOOK_AUTHS as readonly unknown[]).includes(value);
}

/** The wire headers of a webhook delivery, spelled as Rekor's `X-Data-*` integrator headers are. */
export const AUTOMATION_WEBHOOK_HEADERS = {
  signature: 'X-Data-Signature',
  timestamp: 'X-Data-Timestamp',
  id: 'X-Data-Id',
  idempotencyKey: 'X-Data-Idempotency-Key',
} as const;

/** Largest delivery body accepted; a larger one is refused before anything past the edge reads it. */
export const AUTOMATION_WEBHOOK_BODY_MAX_BYTES = 64 * 1024;
/** How far a signed delivery's timestamp may be from now, either way, before it is refused as stale. */
export const AUTOMATION_WEBHOOK_MAX_SKEW_SECONDS = 300;
/** Longest message id or idempotency key a delivery may carry. */
export const AUTOMATION_WEBHOOK_DEDUPE_KEY_MAX = 256;
/** Longest header name a `static_header` trigger may name. */
export const AUTOMATION_WEBHOOK_HEADER_NAME_MAX = 64;

// Where webhook deliveries are received, `AUTOMATION_WEBHOOK_MOUNT`, is declared in `./route-paths`.

/**
 * The path one automation receives its deliveries at. Each hub — a preview and its production
 * hub alike — holds its own automation ids, so each environment has its own URL, and its own secret.
 */
export function automationWebhookPath(hubId: string, automationId: string): string {
  return `${AUTOMATION_WEBHOOK_MOUNT}/${hubId}/${automationId}`;
}

const WEBHOOK_HEADER_NAME_REGEX = /^[A-Za-z0-9-]+$/;
/** Headers a proxy sets or the transport owns: a value there is never the sender's secret. */
const WEBHOOK_RESERVED_HEADERS: ReadonlySet<string> = new Set([
  'host', 'connection', 'cookie', 'transfer-encoding', 'forwarded', 'via', 'true-client-ip', 'x-real-ip',
  ...Object.values(AUTOMATION_WEBHOOK_HEADERS).map((h) => h.toLowerCase()),
]);
const WEBHOOK_RESERVED_HEADER_PREFIXES = ['cf-', 'content-', 'x-forwarded-'];

/** Why `header` cannot carry a `static_header` trigger's secret, or null when it can. */
export function automationWebhookHeaderIssue(header: string): string | null {
  if (header.length > AUTOMATION_WEBHOOK_HEADER_NAME_MAX || !WEBHOOK_HEADER_NAME_REGEX.test(header)) {
    return `"${header}" is not a header name (letters, digits and hyphens, at most ${AUTOMATION_WEBHOOK_HEADER_NAME_MAX})`;
  }
  const lower = header.toLowerCase();
  if (WEBHOOK_RESERVED_HEADERS.has(lower) || WEBHOOK_RESERVED_HEADER_PREFIXES.some((p) => lower.startsWith(p))) {
    return `"${header}" is set by the transport or the signing scheme; name a header of the sender's own`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/**
 * When the automation fires. `schedule` carries a 5-field cron read in `timezone`; `event` names one
 * event of the catalog (`AUTOMATION_EVENT_TYPES`) — of this hub, or, with `source_hub`, of another
 * production hub of the organization (wayai#4931 D12): a cross-hub subscription, which only a
 * PRODUCTION hub's automation holds (owner decision 6). A preview keeps it as config and fires
 * nothing on it; the publish that copies it to production subscribes it, once the publisher may
 * read the source hub and its conversations (`hub:read` and `conversation:read`), and again at
 * every later publish (owner decision 10). The source hub's
 * admins can revoke it (`AutomationEventSubscriber`). `webhook` (task hubs only) fires on each
 * delivery to its URL, its sender proven by `auth` — and, for `static_header`, the `header` that
 * carries the secret.
 */
export const automationTriggerSchema = z.object({
  type: z.enum(AUTOMATION_TRIGGER_TYPES),
  cron: z.string().trim().min(1).max(AUTOMATION_CRON_MAX).optional(),
  timezone: z.string().trim().min(1).optional(),
  event: z.enum(AUTOMATION_EVENT_TYPES).optional(),
  /** The production hub whose events an `event` trigger listens to; absent: this hub's own. */
  source_hub: z.string().uuid().optional(),
  auth: z.enum(AUTOMATION_WEBHOOK_AUTHS).optional(),
  header: z.string().trim().min(1).max(AUTOMATION_WEBHOOK_HEADER_NAME_MAX).optional(),
}).strict();

/**
 * Who each fire is for. `contact_list` names one list by name: one of the hub's own lists
 * (`list`, `hub-contact-lists.ts`) or one of the org's (`org_list`, `contact-book.ts`), never both,
 * so neither shadows the other. Each fire reaches the list's members its hub sees, and a fire that
 * finds no list of that name records `list_not_found`. `none` (task hubs only) is for no one:
 * each fire opens one task.
 */
export const automationTargetSchema = z.object({
  type: z.enum(AUTOMATION_TARGET_TYPES),
  list: hubListNameSchema.optional(),
  org_list: orgListReferenceSchema.optional(),
}).strict();

/**
 * What each fire does, per target. `send_message` sends a WhatsApp template (`template_id`) or a
 * text (`text`) on `channel`; `run_agent` hands `instructions` to the named agent in each contact's
 * conversation on `channel` (its WhatsApp `template_id` opening one whose window is closed) — on a
 * task hub, with no channel, in a task of its own per target.
 */
export const automationActionSchema = z.object({
  type: z.enum(AUTOMATION_ACTION_TYPES),
  channel: z.enum(AUTOMATION_CHANNEL_TYPES).optional(),
  connection_id: z.string().uuid().nullable().optional(),
  template_id: z.string().uuid().nullable().optional(),
  text: z.string().max(AUTOMATION_TEXT_MAX).nullable().optional(),
  agent_id: z.string().uuid().nullable().optional(),
  instructions: z.string().max(AUTOMATION_INSTRUCTIONS_MAX).nullable().optional(),
}).strict();

/**
 * Whether each fire runs — or, on a contact list, whether each of its contacts is started —
 * decided before it is (wayai#4931 D11): `monitor` names one of the hub's monitors, by
 * `agent_name`, on a decisions model (`isDecisionsModel`). Its trigger says which gate it is:
 *   - `automation_fire`, a FIRE gate: asked once per fire, its rules choose one of
 *     `run | run_with_agent(agent) | skip` (`MONITOR_AUTOMATION_FIRE_ACTION_KINDS`); the run
 *     records the decision and its confidence (`AutomationRun.gate_decision`);
 *   - `automation_item`, an ITEM gate (a `contact_list` target only): asked once per contact of
 *     the fire's list, its rules choose one of `start | start_with_agent(agent) | skip`
 *     (`MONITOR_AUTOMATION_ITEM_ACTION_KINDS`); each contact's target records its decision and
 *     confidence.
 * A gate whose call fails or times out does not run what it gates (`gate_failed`).
 */
export const automationGateSchema = z.object({
  monitor: z.string().trim().min(1).max(MONITOR_GATE_AGENT_NAME_MAX),
}).strict();

export type AutomationTrigger = z.infer<typeof automationTriggerSchema>;
export type AutomationTarget = z.infer<typeof automationTargetSchema>;
export type AutomationAction = z.infer<typeof automationActionSchema>;
export type AutomationGate = z.infer<typeof automationGateSchema>;

/**
 * What a fire gate decided, as its run records it (`AutomationRun.gate_decision`) — the
 * `automation_fire` action kinds themselves: `run` fired as configured, `run_with_agent` fired with
 * the agent the rule named (`gate_agent`), `skip` did not fire.
 */
export const AUTOMATION_GATE_DECISIONS = MONITOR_AUTOMATION_FIRE_ACTION_KINDS;
export type AutomationGateDecision = typeof AUTOMATION_GATE_DECISIONS[number];

/**
 * What an item gate decided for one contact, as its run target records it — the
 * `automation_item` action kinds themselves: `start` started the contact's work as configured,
 * `start_with_agent` started it with the agent the rule named, `skip` started nothing for it.
 */
export const AUTOMATION_ITEM_GATE_DECISIONS = MONITOR_AUTOMATION_ITEM_ACTION_KINDS;
export type AutomationItemGateDecision = typeof AUTOMATION_ITEM_GATE_DECISIONS[number];

/**
 * What a gate's monitor is judged by (wayai#4931 D11): the agent's role, the model its
 * `agent_settings` names, and its `monitor_config` (decoded, or as the column's JSON text).
 */
export interface AutomationGateMonitorFacts {
  agent_role: unknown;
  model: string | null;
  monitor_config: unknown;
}

/** The gate facts of an agent row, whichever form its writer holds the columns in. */
export function automationGateMonitorFactsOf(agent: {
  agent_role?: unknown;
  agent_settings?: unknown;
  monitor_config?: unknown;
}): AutomationGateMonitorFacts {
  return {
    agent_role: agent.agent_role,
    model: readAgentSettingsModel(agent.agent_settings),
    monitor_config: agent.monitor_config,
  };
}

/**
 * The gate a monitor's `monitor_config` makes it, by its trigger — `automation_fire` (a fire gate,
 * asked once per fire) or `automation_item` (an item gate, asked once per contact of its list) — or
 * null on any other trigger.
 */
export function automationGateTriggerOf(monitorConfig: unknown): AutomationGateTrigger | null {
  const trigger = resolveMonitorTrigger(decodedMonitorConfig(monitorConfig));
  return isAutomationGateTrigger(trigger) ? trigger : null;
}

/**
 * Why `monitorName` cannot gate an automation whose target is `targetType`, or null when it can
 * (wayai#4931 D11): it must be one of the hub's monitors, bound to a decisions model
 * (`isDecisionsModel`), on `automation_fire` (a fire gate, any target) or on `automation_item` (an
 * item gate, which a `contact_list` target alone has items for). The one rule every save surface
 * and the app's gate picker read; HubDO's re-check before a fire adds what only the fire reads
 * (the monitor enabled, its connection).
 */
export function automationGateMonitorIssue(
  monitorName: string,
  facts: AutomationGateMonitorFacts | undefined,
  targetType: AutomationTargetType,
): string | null {
  if (!facts) return `gate monitor "${monitorName}" does not exist in this hub`;
  if (facts.agent_role !== 'monitor') {
    return `gate "${monitorName}" is a ${String(facts.agent_role)} agent; a gate is a monitor on the automation_fire `
      + 'or automation_item trigger';
  }
  const trigger = resolveMonitorTrigger(decodedMonitorConfig(facts.monitor_config));
  if (!isAutomationGateTrigger(trigger)) {
    return `gate monitor "${monitorName}" is on the "${trigger}" trigger; a gate's monitor is on automation_fire `
      + '(a fire gate) or automation_item (an item gate)';
  }
  if (trigger === 'automation_item' && targetType !== 'contact_list') {
    return `gate monitor "${monitorName}" is an item gate (automation_item), which decides each contact of a list; `
      + 'this automation targets no list, so its gate is a fire gate (automation_fire)';
  }
  if (!isDecisionsModel(facts.model)) {
    return `gate monitor "${monitorName}" is not on a decisions model${facts.model ? ` (model "${facts.model}")` : ''}; a gate runs on one`;
  }
  return null;
}

const automationName = z.string().trim().min(1).max(AUTOMATION_NAME_MAX);
const automationDescription = z.string().max(AUTOMATION_DESCRIPTION_MAX).nullable().optional();

// ---------------------------------------------------------------------------
// Params & Queries
// ---------------------------------------------------------------------------

export const automationIdParam = z.object({
  id: z.string().uuid(),
});

export const automationHubQuery = z.object({
  hub_id: z.string().uuid(),
});

/** The ids a webhook delivery's URL names (`automationWebhookPath`). */
export const automationWebhookParams = z.object({
  hub_id: z.string().uuid(),
  automation_id: z.string().uuid(),
});

/** Most runs one page of an automation's history carries. */
export const AUTOMATION_RUNS_PAGE_MAX = 50;

export const listAutomationRunsQuery = z.object({
  hub_id: z.string().uuid(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(AUTOMATION_RUNS_PAGE_MAX).default(10),
});

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

/**
 * Create body. An automation is always created disabled; `POST /:id/toggle` enables it. Whether
 * an automation fires is derived, never set: it fires while it is enabled, not paused and valid
 * (wayai#4931 D15), and every write re-derives it.
 * Unknown keys are refused rather than dropped, so a field the platform does not run yet is never
 * accepted and silently ignored.
 */
export const createAutomationBody = z.object({
  hub_id: z.string().uuid(),
  name: automationName,
  description: automationDescription,
  /** Absent is `DEFAULT_AUTOMATION_PURPOSE`. */
  purpose: z.enum(AUTOMATION_PURPOSES).optional(),
  trigger: automationTriggerSchema,
  target: automationTargetSchema,
  action: automationActionSchema,
  /** Absent or null: every fire runs. */
  gate: automationGateSchema.nullable().optional(),
}).strict();

/**
 * Update body (the hub rides the `hub_id` query, like every route on one automation). Each of
 * `trigger`, `target` and `action` is replaced whole when present.
 */
export const updateAutomationBody = z.object({
  name: automationName.optional(),
  description: automationDescription,
  purpose: z.enum(AUTOMATION_PURPOSES).optional(),
  trigger: automationTriggerSchema.optional(),
  target: automationTargetSchema.optional(),
  action: automationActionSchema.optional(),
  /** Replaced when present; null removes the gate. */
  gate: automationGateSchema.nullable().optional(),
}).strict();

export type CreateAutomationBody = z.infer<typeof createAutomationBody>;
export type UpdateAutomationBody = z.infer<typeof updateAutomationBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface Automation {
  automation_id: string;
  hub_id: string;
  name: string;
  description: string | null;
  purpose: AutomationPurpose;
  enabled: boolean;
  trigger: AutomationTrigger;
  target: AutomationTarget;
  action: AutomationAction;
  /** The fire gate, or null when every fire runs. */
  gate: AutomationGate | null;
  /**
   * Runtime state, kept apart from the automation's config (it never branches). `next_run_at` is
   * set exactly while a fire is scheduled.
   */
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  /** Set while the automation is paused — the stop a production hub's admin holds (D15). */
  paused_at: string | null;
  /** The user id of whoever paused it. */
  paused_by: string | null;
  /**
   * The last time the automation, enabled and not paused, could not fire, and why — a reference
   * gone (an agent, a template, a connection) or disabled. Cleared by the next fire that runs.
   */
  last_skip_reason: string | null;
  last_skipped_at: string | null;
  /**
   * Whether this automation holds a webhook secret (wayai#4931 D13) — runtime state of its own
   * hub, never copied by a publish, so a production copy holds none until an admin of it rotates
   * one. Never the secret itself. False on every other trigger.
   */
  webhook_secret_set: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * A webhook automation's secret, revealed by the one call that creates it — the create of a
 * webhook automation, or a rotate — and never again: no read returns it.
 */
export interface AutomationWebhookReveal {
  /**
   * Where the sender delivers (`automationWebhookPath` on the backend's origin); null when the
   * backend's own origin is not configured.
   */
  url: string | null;
  secret: string;
}

/**
 * What a webhook delivery is answered with when it is accepted (202): queued now, or — `duplicate`
 * — a repeat of one accepted before, which queues nothing.
 */
export interface AutomationWebhookAcceptedResponse {
  ok: true;
  duplicate: boolean;
}

export interface ListAutomationsResponse {
  automations: Automation[];
}

/** A connection a preview automation uses whose credential is also production's (D9). */
export interface SharedProductionConnection {
  connection_id: string;
  connection_display_name: string | null;
}

export interface GetAutomationResponse {
  automation: Automation;
  /**
   * On a preview hub: the connections the automation acts through — its own and its agent's,
   * tools included — whose credential is also production's, so a preview fire reaches the
   * systems production reaches. Empty on a production hub.
   */
  shared_production_connections: SharedProductionConnection[];
  /**
   * A webhook automation's delivery URL; null on every other trigger, and on a webhook automation
   * when the backend's own origin is not configured (`AutomationWebhookReveal.url`).
   */
  webhook_url: string | null;
}

export interface CreateAutomationResponse {
  automation_id: string;
  /** A webhook automation's URL and its new secret, shown this once. Absent on every other trigger. */
  webhook?: AutomationWebhookReveal;
}

/** `POST /:id/webhook-secret/rotate`: the new secret, shown this once; the old one stops verifying. */
export interface RotateAutomationWebhookSecretResponse {
  ok: true;
  webhook: AutomationWebhookReveal;
}

export interface ToggleAutomationResponse {
  ok: true;
  enabled: boolean;
  next_run_at: string | null;
}

export interface RunAutomationNowResponse {
  ok: true;
  run_id: string;
}

/** `POST /:id/pause` and `POST /:id/resume`. */
export interface PauseAutomationResponse {
  ok: true;
  paused_at: string | null;
  next_run_at: string | null;
}

/**
 * Why a fire reached no target, or stopped before the end of its list, as its run records it:
 *   - `list_not_found`: the hub list or org list it names does not exist (or no longer does, for a
 *     run whose list is deleted while it is read). Also raises the hub's alert.
 *   - `list_unavailable`: the contact book could not be read (or the runner that reads the list
 *     could not be started); the next fire reads again.
 *   - `gate_failed`: its fire gate's call failed or timed out, so the fire did not run — a gate
 *     fails closed, wayai#4931 D11; a provider's 4xx also raises the connection's alert.
 *   - `gate_agent_invalid`: its gate chose `run_with_agent` with an agent this automation cannot
 *     run — gone, disabled, one that cannot take an automation's turn (not a `pilot` or
 *     `pilot_specialist`), or an automation that runs no agent — so the fire did not run, and the
 *     automation's alert names why.
 *   - `quota_exceeded`: the org's operations quota was reached while the run read its list (it is
 *     asked before each page of contacts is started); the contacts of that page are recorded
 *     skipped and no later page is read. The next fire is the schedule's first after the quota
 *     resets.
 *   - `run_stopped`: the automation was paused, deleted, or left unable to fire while the run read
 *     its list; no later page is read.
 *   - `run_interrupted`: the hub was reset (or the fire threw) mid-fire, so nothing recorded how the fire
 *     ended; the recovery of its claim ended the run (wayai-pro/wayai#5083 decision 189). Its task, or its
 *     contacts, may or may not have been reached; the fire is never sent again.
 *   - `list_over_cap`: recorded only by runs from before a list was read a page at a time
 *     (wayai#4931 PR 8), which refused a list past `platform_config.automation_list_max_contacts`.
 *     No run records it now.
 * A run that stopped keeps what it reached: its targets so far, and its counts.
 */
export const AUTOMATION_RUN_ERROR_CODES = [
  'list_not_found', 'list_over_cap', 'list_unavailable', 'gate_failed', 'gate_agent_invalid', 'quota_exceeded', 'run_stopped', 'run_interrupted',
] as const;
export type AutomationRunErrorCode = typeof AUTOMATION_RUN_ERROR_CODES[number];

/**
 * The hub Status & Notices alert category each run error that raises one raises (a `warn` alert of
 * no connection, `HubAlert.category`). `list_over_cap` is raised by no run now (its run error
 * code's note): an alert raised before clears with the hub's stale alerts, a day after it was last
 * seen (`listActiveAlerts`), and its hint says there is nothing to change.
 */
export const AUTOMATION_ALERT_CATEGORIES = {
  list_not_found: 'automation_list_not_found',
  list_over_cap: 'automation_list_over_cap',
} as const satisfies Partial<Record<AutomationRunErrorCode, string>>;
export type AutomationAlertCategory = typeof AUTOMATION_ALERT_CATEGORIES[keyof typeof AUTOMATION_ALERT_CATEGORIES];

/**
 * The category of the alert an automation that is enabled and not paused but cannot fire raises
 * (wayai#4931 D15): one active per automation (`HubAlert` of no connection), its `detail` the reason
 * naming the automation. It clears once the automation fires, or is paused, disabled or fixed.
 */
export const AUTOMATION_BLOCKED_ALERT_CATEGORY = 'automation_blocked';

export interface AutomationRun {
  run_id: string;
  automation_id: string;
  status: AutomationRunStatus;
  /** Why the fire reached no target, or null. */
  error_code: AutomationRunErrorCode | null;
  total_targets: number;
  succeeded: number;
  failed: number;
  skipped: number;
  /** What the fire gate decided, or null for a fire with no gate or whose gate failed. */
  gate_decision: AutomationGateDecision | null;
  /** The agent a `run_with_agent` decision named, or null. */
  gate_agent: string | null;
  /**
   * How sure the gate was, 0 to 1: the lowest confidence among the answers its decision read —
   * the matched rule's fields, or every answered field when no rule matched. Null when no
   * answer carried one.
   */
  gate_confidence: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface ListAutomationRunsResponse {
  runs: AutomationRun[];
  total: number;
  page: number;
  per_page: number;
}

// ---------------------------------------------------------------------------
// Cross-hub event subscribers (wayai#4931 D12)
// ---------------------------------------------------------------------------

/**
 * Most subscriptions one hub's events carry: every subscriber automation of every other hub. It
 * bounds the subscriber list on both axes, and the deliveries one event queues.
 */
export const AUTOMATION_EVENT_SUBSCRIPTIONS_PER_HUB_MAX = 100;

/** `/api/automations/event-subscribers/:subscriber_hub_id/revoke`. */
export const automationEventSubscriberParam = z.object({
  subscriber_hub_id: z.string().uuid(),
});

/**
 * One automation of another production hub that runs on this hub's events (a subscription, D12),
 * as the hub's subscriber list shows it. The hub and automation names are the subscriber's, as of
 * its last publish. `last_delivery_error` is set when an event could not be delivered to it after
 * every attempt, and cleared by the next delivery that lands.
 */
export interface AutomationEventSubscriber {
  subscriber_hub_id: string;
  subscriber_hub_name: string | null;
  automation_id: string;
  automation_name: string | null;
  event: AutomationEventType;
  created_at: string;
  updated_at: string;
  last_delivery_error: string | null;
  last_delivery_error_at: string | null;
}

/** A hub the source hub's admins revoked: none of its automations hears this hub's events again. */
export interface AutomationEventSubscriberRevocation {
  subscriber_hub_id: string;
  revoked_at: string;
  revoked_by: string | null;
}

export interface ListAutomationEventSubscribersResponse {
  subscribers: AutomationEventSubscriber[];
  revoked: AutomationEventSubscriberRevocation[];
}

export interface RevokeAutomationEventSubscriberResponse {
  ok: true;
  revoked_at: string;
  /** The subscriber's automations whose subscriptions the revocation removed. */
  removed: number;
}
